import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslationService } from '@lib/i18n/data-access';
import { ToastService } from '@platform-workspace/design-system-v2';
import { ErrorCode } from '@shared/utils/enums';
import { BaseResponse, ErrorMessageStrategyResult, ResponseError } from '@shared/utils/interfaces';
import { catchError, mergeMap, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ERROR_MESSAGE_STRATEGIES } from './error-strategies-map.constant';

export const errorInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const trustedUrls: string[] = [environment.apiUrl, environment.apiUrlV2];
  const errorCodeBlacklist = [
    ErrorCode.UserDeactivated,
    ErrorCode.UserLogin,
    ErrorCode.UserDeactivatedAuth,
    ErrorCode.KycConstraintViolation,
  ];

  const translationService = inject(TranslationService);
  const toastService = inject(ToastService);

  return next(req).pipe(
    mergeMap((event: HttpEvent<any>) => {
      const isTrustedResponse = event instanceof HttpResponse && trustedUrls.some((url) => req.url.startsWith(url));

      if (isTrustedResponse) {
        const responseBody = event.body as BaseResponse<unknown>;

        if (responseBody.success === false) {
          const error = responseBody.errors?.[0];

          if (error && !errorCodeBlacklist.includes(error.code)) {
            const strategy = ERROR_MESSAGE_STRATEGIES[error.code];

            let translationKey = `errors.${error.code}`;
            let translationParams: Record<string, unknown> | undefined;

            if (strategy) {
              const result: ErrorMessageStrategyResult = strategy(error, req, event);
              translationKey = result.translationKey;
              translationParams = result.translationParams;
            } else {
              console.warn(`[errorInterceptor] No error message strategy found for "${error.code}".`);
            }

            toastService.showError(translationService.translate(translationKey, translationParams));
          }

          if (error) {
            error.siblingErrors = responseBody.errors?.slice(1);
          }

          // Propagate the structured ResponseError so consumers (e.g. interceptors, login) can read `error.code`.
          return throwError(() => error ?? new Error(req.url));
        }
      }

      return of(event);
    }),
    catchError((error: HttpErrorResponse | ResponseError) => {
      // Preserve the structured ResponseError emitted above so consumers can read `error.code`.
      if (error && 'code' in error) {
        return throwError(() => error);
      }

      if (error.status === 0) {
        toastService.showError(translationService.translate('errors.vpnConnection'));
      }

      return throwError(() => new Error(error?.message || req.url));
    }),
  );
};
